// Shared source-connect dispatch — the network/extension side of connecting a chat
// source, moved out of SourcesSurface so the onboarding chat branch reuses the exact
// same connect POST + extension backfill handshake (no behavioral fork).

import { apiFetch, ApiError } from './api';
import { CONSENT_SOURCES } from './sources';
import { CHROME_STORE_URL } from './constants';

export type ChatSourceId = 'chatgpt' | 'claude' | 'perplexity';

export interface BackfillTriggerResult {
  ok: boolean;
  // 'not_connected' = extension present but no Terum token; 'no_extension' = no ack
  // (not installed / content script absent); otherwise the background's error string.
  error?: string;
}

/**
 * bug-207: the server gates chat-source connect on a handshaken extension and replies
 * 409 { code: 'extension_required' } when it's missing — the extension is the only thing
 * that can capture/backfill a chat source. We translate that gate into a typed error that
 * carries the Chrome Web Store install URL, so the connect surfaces can point the user
 * straight at the install step instead of showing an opaque "API error: 409 Conflict".
 */
export class ExtensionRequiredError extends Error {
  readonly installUrl = CHROME_STORE_URL;
  constructor(
    message = 'Install and connect the Terum extension before connecting this source, then try again.'
  ) {
    super(message);
    this.name = 'ExtensionRequiredError';
  }
}

// How long to wait for the extension's terum-resync-response ack before treating the
// extension as absent. content-announce relays to the background and echoes its reply;
// a healthy extension answers within a few ms, so this only ever elapses when nothing
// is listening (extension not installed).
const RESYNC_ACK_TIMEOUT_MS = 6000;

/**
 * bug-207: re-arm the extension backfill for `site` as a VERIFIED round-trip. Posts the
 * resync with a correlation id and WAITS for the extension's `terum-resync-response` ack
 * before doing anything else. Only once the extension confirms it started the run do we
 * record the durable in-progress marker — so the sources card never shows a fake
 * CAPTURING when the extension is absent or signed out. No ack (timeout) → no_extension;
 * an `ok:false` ack carries the background's reason (e.g. not_connected). This is the
 * shared chokepoint for every backfill trigger (connect-with-history, manual LOAD/RETRY,
 * drift rebind), reused by both SourcesSurface and the onboarding chat branch.
 */
export async function triggerBackfill(site: string): Promise<BackfillTriggerResult> {
  const requestId =
    typeof crypto !== 'undefined' && 'randomUUID' in crypto
      ? crypto.randomUUID()
      : `rq-${Date.now()}-${Math.random().toString(36).slice(2)}`;

  const ack = await new Promise<BackfillTriggerResult>((resolve) => {
    let settled = false;
    const onMessage = (event: MessageEvent) => {
      if (event.origin !== window.location.origin) return;
      if (event.data?.type === 'terum-resync-response' && event.data.requestId === requestId) {
        if (settled) return;
        settled = true;
        clearTimeout(timer);
        window.removeEventListener('message', onMessage);
        resolve({ ok: event.data.ok === true, error: event.data.error ?? undefined });
      }
    };
    const timer = setTimeout(() => {
      if (settled) return;
      settled = true;
      window.removeEventListener('message', onMessage);
      resolve({ ok: false, error: 'no_extension' });
    }, RESYNC_ACK_TIMEOUT_MS);
    window.addEventListener('message', onMessage);
    // Credentials come from the extension's stored handshake, not this message.
    window.postMessage({ type: 'terum-resync-backfill', site, requestId }, window.location.origin);
  });

  if (!ack.ok) return ack;

  // Backfill genuinely started — NOW record the durable in-progress marker. Best-effort:
  // a marker-write failure must not undo the import that's already running.
  try {
    await apiFetch('/api/surfaces/sources', {
      method: 'POST',
      body: JSON.stringify({ sourceId: site, action: 'mark-backfill-started' }),
    });
  } catch (err) {
    console.warn('[connect-source] mark-backfill-started failed', err);
  }
  return { ok: true };
}

/**
 * Connect a chat source: POST the connect action with consent flags, then — only
 * when the user opted to import history (`consentPast`) — re-arm the extension
 * backfill. Mirrors SourcesSurface.connectSource's network behavior exactly
 * (including the CONSENT_SOURCES guard). UI orchestration (loading/error/cache
 * invalidation) stays with the caller.
 */
export async function connectChatSource(opts: {
  sourceId: ChatSourceId;
  consentFuture: boolean;
  consentPast: boolean;
}): Promise<void> {
  try {
    await apiFetch('/api/surfaces/sources', {
      method: 'POST',
      body: JSON.stringify({
        sourceId: opts.sourceId,
        action: 'connect',
        consentFuture: opts.consentFuture,
        consentPast: opts.consentPast,
      }),
    });
  } catch (err) {
    // bug-207: surface the extension-required gate as an actionable, typed error (carrying
    // the install link) instead of the opaque "API error: 409 Conflict" apiFetch throws.
    if (err instanceof ApiError && err.code === 'extension_required') {
      throw new ExtensionRequiredError();
    }
    throw err;
  }
  if (opts.consentPast && CONSENT_SOURCES.includes(opts.sourceId)) {
    const result = await triggerBackfill(opts.sourceId);
    if (!result.ok) {
      // The source IS connected (future capture works), but the history import didn't
      // start. Surface it so the user can reconnect the extension and retry via the card's
      // "Load last 30 days" — never silently swallow a missed import (bug-207).
      throw new Error(
        result.error === 'not_connected'
          ? 'Connected, but the Terum extension isn’t signed in — reconnect it, then use “Load last 30 days”.'
          : 'Connected, but couldn’t reach the Terum extension to import your history — make sure it’s installed and connected, then use “Load last 30 days”.'
      );
    }
  }
}
